import { useCallback, useState, type FormEvent } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { ArrowRight, LockKeyhole, WalletCards } from 'lucide-react'
import { Turnstile } from '../components/Turnstile'
import { Button, Notice } from '../components/Ui'
import { useAuth } from '../state/auth'
import { BrandLogo } from '../components/BrandLogo'

type Mode = 'sign-in' | 'sign-up' | 'forgot'

function GoogleMark() {
  return <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M21.35 12.24c0-.71-.06-1.4-.18-2.06H12v3.89h5.24a4.49 4.49 0 0 1-1.94 2.95v2.45h3.14c1.84-1.7 2.91-4.2 2.91-7.23Z" />
    <path fill="#34A853" d="M12 21.75c2.63 0 4.84-.87 6.45-2.36l-3.14-2.45c-.87.58-1.98.93-3.31.93-2.53 0-4.67-1.71-5.43-4.01H3.34v2.52A9.75 9.75 0 0 0 12 21.75Z" />
    <path fill="#FBBC05" d="M6.57 13.86a5.86 5.86 0 0 1 0-3.72V7.62H3.34a9.75 9.75 0 0 0 0 8.76l3.23-2.52Z" />
    <path fill="#EA4335" d="M12 6.13c1.43 0 2.72.49 3.73 1.48l2.8-2.8A9.35 9.35 0 0 0 12 2.25a9.75 9.75 0 0 0-8.66 5.37l3.23 2.52C7.33 7.84 9.47 6.13 12 6.13Z" />
  </svg>
}

export function LoginPage() {
  const { configured, user, loading, authError, clearAuthError, signIn, signUp, signInWithGoogle, sendPasswordReset } = useAuth()
  const [mode, setMode] = useState<Mode>('sign-in')
  const [redirectError, setRedirectError] = useState(() => {
    const url = new URL(window.location.href)
    return url.searchParams.has('error') || new URLSearchParams(url.hash.slice(1)).has('error')
      ? 'Masuk dengan Google belum berhasil. Coba lagi atau gunakan email.'
      : ''
  })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [submitting, setSubmitting] = useState<'email' | 'google' | null>(null)
  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaResetKey, setCaptchaResetKey] = useState(0)
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() ?? ''
  const captchaEnabled = Boolean(turnstileSiteKey)
  const receiveCaptchaToken = useCallback((token: string) => setCaptchaToken(token), [])
  const reportCaptchaError = useCallback((message: string) => setError(message), [])

  if (!loading && user) return <Navigate to="/app" replace />

  function changeMode(next: Mode) {
    setMode(next)
    setError('')
    setSuccess('')
    setRedirectError('')
    clearAuthError()
    setCaptchaToken('')
    setCaptchaResetKey((current) => current + 1)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError('')
    setSuccess('')
    setRedirectError('')
    clearAuthError()
    setSubmitting('email')
    try {
      if (mode === 'forgot') {
        if (captchaToken) await sendPasswordReset(email, captchaToken)
        else await sendPasswordReset(email)
        setSuccess('Jika email terdaftar, tautan atur ulang kata sandi akan dikirim. Periksa kotak masuk dan folder spam.')
      } else if (mode === 'sign-up') {
        const result = captchaToken ? await signUp(email, password, captchaToken) : await signUp(email, password)
        if (result === 'check-email') setSuccess('Cek email kamu untuk verifikasi akun, lalu masuk ke Dhuwit. Periksa juga folder spam.')
      } else {
        if (captchaToken) await signIn(email, password, captchaToken)
        else await signIn(email, password)
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Permintaan gagal. Coba lagi.')
    } finally {
      setSubmitting(null)
      if (captchaEnabled) {
        setCaptchaToken('')
        setCaptchaResetKey((current) => current + 1)
      }
    }
  }

  async function startGoogle() {
    setError('')
    setSuccess('')
    setRedirectError('')
    clearAuthError()
    setSubmitting('google')
    try {
      await signInWithGoogle()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Google belum bisa digunakan. Coba lagi atau pakai email.')
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <main className="login-page">
      <section className="login-intro" aria-label="Tentang Dhuwit">
        <Link className="sidebar__brand" to="/" aria-label="Kembali ke halaman utama Dhuwit"><BrandLogo /><span><strong>Dhuwit</strong><small>Keuangan jadi mudah</small></span></Link>
        <div className="login-intro__content">
          <span className="eyebrow">Keuangan yang terasa ringan</span>
          <h1>Kenali arah<br />dhuwit kamu.</h1>
          <p>Catat pemasukan dan pengeluaran, atur dompet serta budget, lalu lihat gambaran uangmu dalam satu tempat.</p>
          <div className="login-intro__feature"><WalletCards size={20} aria-hidden="true" /><span>Satu akun, catatan keuanganmu sendiri.</span></div>
        </div>
        <small>Akun pribadi · Data tiap akun terpisah</small>
      </section>
      <section className="login-panel">
        <div className="login-form">
          <div className="login-form__header"><span className="eyebrow">Selamat datang di Dhuwit</span><h2>{mode === 'sign-up' ? 'Buat akun Dhuwit' : mode === 'forgot' ? 'Atur ulang kata sandi' : 'Masuk ke akunmu'}</h2><p>{mode === 'sign-up' ? 'Mulai kelola dhuwitmu dengan Google atau email.' : mode === 'forgot' ? 'Masukkan email akunmu untuk menerima tautan pemulihan.' : 'Lanjutkan catatan keuanganmu di sini.'}</p></div>
          <div className="login-tabs" aria-label="Pilih masuk atau daftar">
            <button type="button" className={mode !== 'sign-up' ? 'active' : ''} aria-pressed={mode !== 'sign-up'} onClick={() => changeMode('sign-in')}>Masuk</button>
            <button type="button" className={mode === 'sign-up' ? 'active' : ''} aria-pressed={mode === 'sign-up'} onClick={() => changeMode('sign-up')}>Daftar</button>
          </div>
          {!configured ? <Notice tone="danger"><span><strong>Supabase belum terhubung.</strong><br />Konfigurasi proyek diperlukan sebelum pendaftaran bisa dipakai.</span></Notice> : null}
          {error || authError || redirectError ? <Notice tone="danger"><span>{error || authError || redirectError}</span></Notice> : null}
          {success ? <Notice tone="success"><span>{success}</span></Notice> : null}
          {mode !== 'forgot' ? <><Button className="login-google" type="button" variant="secondary" onClick={() => void startGoogle()} loading={submitting === 'google'} disabled={!configured || submitting !== null} loadingLabel="Menghubungkan Google..."><GoogleMark />Lanjutkan dengan Google</Button><div className="login-divider"><span>atau pakai email</span></div></> : null}
          <form className="login-email-form" onSubmit={(event) => void submit(event)}>
            <label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="nama@email.com" required /></label>
            {mode !== 'forgot' ? <label>Kata sandi<input type="password" autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'} value={password} onChange={(event) => setPassword(event.target.value)} placeholder={mode === 'sign-up' ? 'Minimal 8 karakter' : 'Masukkan kata sandi'} minLength={mode === 'sign-up' ? 8 : undefined} required /></label> : null}
            {mode === 'sign-in' ? <button type="button" className="login-forgot" onClick={() => changeMode('forgot')}>Lupa kata sandi?</button> : null}
            {captchaEnabled ? <Turnstile siteKey={turnstileSiteKey} resetKey={captchaResetKey} onToken={receiveCaptchaToken} onError={reportCaptchaError} /> : null}
            <Button type="submit" loading={submitting === 'email'} disabled={!configured || submitting !== null || (captchaEnabled && !captchaToken)} loadingLabel={mode === 'sign-up' ? 'Mendaftarkan...' : 'Memproses...'}>{mode === 'sign-up' ? 'Buat akun' : mode === 'forgot' ? 'Kirim tautan pemulihan' : 'Masuk'}<ArrowRight size={17} aria-hidden="true" /></Button>
          </form>
          <p className="form-note"><LockKeyhole size={15} aria-hidden="true" />{mode === 'sign-up' ? 'Pendaftaran email memerlukan verifikasi kotak masuk.' : mode === 'forgot' ? 'Tautan pemulihan dikirim hanya lewat email.' : 'Catatan keuangan hanya terlihat oleh akunmu.'}</p>
          <p className="login-privacy">Sebelum mendaftar, baca <Link to="/privacy">kebijakan privasi Dhuwit</Link>. Butuh bantuan? <a href="mailto:apabaetone@gmail.com">Hubungi pengelola</a>.</p>
        </div>
      </section>
    </main>
  )
}
